/**
 * Brand accent colours, made readable in both themes.
 *
 * `brands.accent_hex` holds one colour per label, and two of the three are very
 * dark by design: AS Colour is #16181d and Syzmik is #8a3f00. As a background
 * with white text they are fine. As *text* they fail badly on a dark page, and
 * AS Colour effectively disappears.
 *
 * Rather than add a column to a schema the mobile team also consumes, this
 * derives a light-theme and a dark-theme variant from the single stored value
 * and lets CSS pick. The brand still reads as itself; it just stops being
 * invisible at night.
 */

function channels(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (c: number) => Math.max(0, Math.min(255, Math.round(c)));
  return (
    '#' +
    ((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1)
  );
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function mix(hex: string, target: [number, number, number], amount: number): string {
  const [r, g, b] = channels(hex);
  return toHex(
    r + (target[0] - r) * amount,
    g + (target[1] - g) * amount,
    b + (target[2] - b) * amount,
  );
}

const WHITE: [number, number, number] = [255, 255, 255];
const BLACK: [number, number, number] = [0, 0, 0];

/**
 * A pair of variants for the same brand colour.
 *
 * Light: dark enough to clear 4.5:1 on the near-white page, so a mid-tone brand
 * gets deepened rather than left washed out.
 * Dark: light enough to clear 4.5:1 on the near-black page.
 */
export function accentPair(hex: string): { light: string; dark: string } {
  // Against #f4f5f7 (L 0.912), text needs L <= ~0.164 for 4.5:1.
  let light = hex;
  for (let i = 0; i < 12 && luminance(light) > 0.164; i++) {
    light = mix(light, BLACK, 0.12);
  }

  // Against #191c21 (L 0.0115), text needs L >= ~0.222 for 4.5:1.
  let dark = hex;
  for (let i = 0; i < 20 && luminance(dark) < 0.24; i++) {
    dark = mix(dark, WHITE, 0.14);
  }

  return { light, dark };
}

/**
 * Inline style for accent-coloured text. Pair it with `className="accent-text"`,
 * which reads whichever custom property the active theme selects.
 */
export function accentTextStyle(hex: string): React.CSSProperties {
  const { light, dark } = accentPair(hex);
  return {
    ['--accent-light' as string]: light,
    ['--accent-dark' as string]: dark,
  };
}
